import React from 'react';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Line,
	LineChart,
	Area,
	AreaChart,
	Pie,
	PieChart,
	XAxis,
	YAxis
} from 'recharts';
import T from 'Components/Helpers/translate';
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	type ChartConfig
} from 'App/shadcn/ui/chart';
import { currencyFormat, numberFormat } from 'App/babel/components/Filters/Currencies';
import { CHART_TYPES, type ChartSpec, type ChartType } from './types';

const DONUT_COLORS = [
	'hsl(195 70% 35%)',
	'hsl(35 85% 55%)',
	'hsl(155 50% 40%)',
	'hsl(280 50% 55%)',
	'hsl(15 75% 55%)',
	'hsl(210 60% 50%)',
	'hsl(60 70% 45%)',
	'hsl(330 55% 55%)'
];

function formatValue(n: number, currency?: string): string {
	return currency ? currencyFormat(n, currency) : numberFormat(n);
}

const ALLOWED_CHART_TYPES = new Set<string>(CHART_TYPES);

export function normalizeChartSpec(raw: unknown): ChartSpec {
	const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
	const chartType =
		typeof r.chartType === 'string' && ALLOWED_CHART_TYPES.has(r.chartType)
			? (r.chartType as ChartType)
			: 'table';
	const title = typeof r.title === 'string' && r.title.length > 0 ? r.title : '';
	const rows = Array.isArray(r.rows)
		? (r.rows as unknown[])
				.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
				.map(row => ({
					key: String(row.key ?? ''),
					value: Number(row.value ?? 0)
				}))
		: [];
	return {
		chartType,
		title,
		rows,
		xLabel: typeof r.xLabel === 'string' ? r.xLabel : undefined,
		yLabel: typeof r.yLabel === 'string' ? r.yLabel : undefined,
		currency: typeof r.currency === 'string' ? r.currency : undefined
	};
}

const ChartBody = ({ spec }: { spec: ChartSpec }) => {
	const { chartType, rows, currency, yLabel } = spec;

	if (!rows || rows.length === 0) {
		return <div className="px-1 py-4 text-body-sm text-muted-foreground">{T('default.noData')}</div>;
	}

	if (chartType === 'bigNumber') {
		const total = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
		return (
			<div className="px-2 py-4 flex flex-col gap-2">
				{yLabel && <div className="text-body-sm text-muted-foreground">{yLabel}</div>}
				<div className="text-headline-lg font-semibold text-foreground tabular-nums">
					{formatValue(total, currency)}
				</div>
				{rows.length > 1 && (
					<div className="text-body-sm text-muted-foreground">
						{rows.length} {T('default.rows').toLowerCase()}
					</div>
				)}
			</div>
		);
	}

	if (chartType === 'table') {
		return (
			<div className="px-1 py-2 overflow-x-auto">
				<table className="w-full text-body-sm">
					<thead className="text-muted-foreground">
						<tr className="border-b border-border/40">
							<th className="text-left py-2 font-medium">{spec.xLabel ?? T('default.name')}</th>
							<th className="text-right py-2 font-medium">{yLabel ?? T('default.value')}</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((r, i) => (
							<tr key={`${r.key}-${i}`} className="border-b border-border/20">
								<td className="py-2 text-foreground">{r.key}</td>
								<td className="py-2 text-right tabular-nums text-foreground">
									{formatValue(r.value, currency)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	const config: ChartConfig = {
		value: {
			label: yLabel ?? (T('default.value') as string),
			color: 'hsl(195 70% 35%)'
		}
	};

	if (chartType === 'donut') {
		const donutConfig: ChartConfig = {};
		rows.forEach((r, i) => {
			donutConfig[r.key] = {
				label: r.key,
				color: DONUT_COLORS[i % DONUT_COLORS.length]
			};
		});
		return (
			<div style={{ height: 320 }}>
				<ChartContainer config={donutConfig} className="h-full w-full aspect-auto">
					<PieChart>
						<ChartTooltip
							content={
								<ChartTooltipContent
									formatter={value => formatValue(Number(value), currency)}
								/>
							}
						/>
						<Pie data={rows} dataKey="value" nameKey="key" outerRadius={120} innerRadius={70}>
							{rows.map((r, i) => (
								<Cell key={r.key} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
							))}
						</Pie>
					</PieChart>
				</ChartContainer>
			</div>
		);
	}

	const SeriesChart = chartType === 'line' ? LineChart : chartType === 'area' ? AreaChart : BarChart;

	return (
		<div style={{ height: 320 }}>
			<ChartContainer config={config} className="h-full w-full aspect-auto">
				<SeriesChart data={rows} margin={{ left: 8, right: 16, top: 16, bottom: 8 }}>
					<CartesianGrid vertical={false} strokeDasharray="3 3" />
					<XAxis dataKey="key" tickLine={false} axisLine={false} fontSize={11} />
					<YAxis
						tickLine={false}
						axisLine={false}
						fontSize={11}
						width={60}
						tickFormatter={v => formatValue(Number(v), currency)}
					/>
					<ChartTooltip
						content={
							<ChartTooltipContent formatter={value => formatValue(Number(value), currency)} />
						}
					/>
					{chartType === 'line' && (
						<Line type="monotone" dataKey="value" stroke="var(--color-value)" strokeWidth={2} dot={false} />
					)}
					{chartType === 'area' && (
						<Area type="monotone" dataKey="value" stroke="var(--color-value)" fill="var(--color-value)" fillOpacity={0.2} />
					)}
					{chartType === 'bar' && <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} />}
				</SeriesChart>
			</ChartContainer>
		</div>
	);
};

const CARD_STYLES = `
.ws-chart-card {
	width: 100%;
	border: 1px solid rgba(0,0,0,0.06);
	border-radius: 14px;
	background: var(--ws-panel, #fbfbfa);
	box-shadow: 0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.03);
	overflow: hidden;
	display: flex;
	flex-direction: column;
}
.ws-chart-card__header {
	padding: 14px 18px 6px;
	display: flex;
	flex-direction: column;
	gap: 2px;
}
.ws-chart-card__eyebrow {
	font-size: 11px;
	font-weight: 500;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--ws-ink-4, #8b929a);
}
.ws-chart-card__title {
	font-size: 15px;
	font-weight: 600;
	line-height: 1.3;
	color: var(--ws-ink, #1d252e);
	margin: 0;
}
.ws-chart-card__body {
	padding: 6px 18px 16px;
}
`;

const ChartCard = ({ spec }: { spec: ChartSpec }) => {
	return (
		<>
			<style>{CARD_STYLES}</style>
			<div className="ws-chart-card">
				<div className="ws-chart-card__header">
					<span className="ws-chart-card__eyebrow">{T(`assistant.chart.${spec.chartType}`)}</span>
					{spec.title && <h3 className="ws-chart-card__title">{spec.title}</h3>}
				</div>
				<div className="ws-chart-card__body">
					<ChartBody spec={spec} />
				</div>
			</div>
		</>
	);
};

export default ChartCard;
